import React from "react";
import styled from "styled-components";
import { Card, CardTitle, CardHint, EmptyState, Muted } from "../shared";
import { BarDatum, compact } from "./homeDerive";

const ChartRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 6px;
  height: 150px;
`;

const BarCol = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  height: 100%;
`;

const BarValue = styled.span`
  font-family: var(--font-sans);
  font-size: 10px;
  color: var(--color-text-secondary);
  white-space: nowrap;
`;

const Bar = styled.button<{ $pct: number; $winner: boolean; $selected: boolean }>`
  width: 100%;
  border: none;
  cursor: pointer;
  padding: 0;
  border-radius: 4px 4px 0 0;
  height: ${(p) => Math.max(4, p.$pct)}%;
  background: ${(p) =>
    p.$winner ? "var(--color-info)" : "var(--color-border)"};
  outline: ${(p) =>
    p.$selected ? "2px solid var(--color-text)" : "none"};
  outline-offset: 1px;
  transition: background 0.15s ease;

  &:hover {
    background: ${(p) =>
      p.$winner ? "var(--color-info)" : "var(--color-text-tertiary)"};
  }
`;

const DateLabel = styled.span`
  font-family: var(--font-sans);
  font-size: 9px;
  color: var(--color-text-tertiary);
  white-space: nowrap;
`;

export type PerformanceBarsProps = {
  data: BarDatum[];
  threshold: number;
  selectedPostId: string | null;
  onSelect: (postId: string) => void;
};

export const PerformanceBars: React.FC<PerformanceBarsProps> = ({
  data,
  threshold,
  selectedPostId,
  onSelect,
}) => {
  const maxViews = Math.max(1, ...data.map((d) => d.views));
  return (
    <Card>
      <CardTitle>
        Recent post performance
        {data.length > 0 && <Muted>last {data.length} posts by views</Muted>}
      </CardTitle>
      {data.length === 0 ? (
        <EmptyState>
          Views land here after your first Instagram or Facebook post publishes.
        </EmptyState>
      ) : (
        <>
          <ChartRow>
            {data.map((d) => (
              <BarCol key={d.post.id}>
                <BarValue>{compact(d.views)}</BarValue>
                <Bar
                  $pct={(d.views / maxViews) * 100}
                  $winner={d.views >= threshold}
                  $selected={selectedPostId === d.post.id}
                  onClick={() => onSelect(d.post.id)}
                  aria-label={`Post from ${d.dateLabel}, ${compact(d.views)} views`}
                />
                <DateLabel>{d.dateLabel}</DateLabel>
              </BarCol>
            ))}
          </ChartRow>
          <CardHint>
            Colored bars beat your winner threshold. Click a bar for the post
            and its link.
          </CardHint>
        </>
      )}
    </Card>
  );
};

export default PerformanceBars;
